/**
 * Toast component — shared types and Zod prop schema.
 *
 * A non-interactive informational banner displaying an icon alongside
 * a primary text message and an optional secondary line of text.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { typography } from '../../elements/typography';
import { spacing } from '../../elements/spacing';
import { radius } from '../../elements/radius';
import { IconNameEnum } from '../Icon/Icon';

// Surface-relationship descriptor (ADR-020 Rule 4): `dark` inverts the default
// foreground for placement on a dark surface. Explicit colour props still win.
export const ToastTone = z.enum(['light', 'dark']);
export type ToastTone = z.infer<typeof ToastTone>;

export const toastPropsSchema = z.object({
  text: z.string(),
  secondaryText: z.string().optional(),
  iconName: IconNameEnum,
  tone: ToastTone.default('light'),
  backgroundColor: z.string().optional(),
  iconColor: z.string().optional(),
  textColor: z.string().optional(),
  secondaryTextColor: z.string().optional(),
});

// Input shape so the defaulted `tone` field stays optional for callers.
export type ToastProps = z.input<typeof toastPropsSchema>;

/** Default colours from the Figma design system. */
export const TOAST_DEFAULT_BACKGROUND = colors.background.secondary;
export const TOAST_DEFAULT_ICON_COLOR = colors.icon.primary;
export const TOAST_DEFAULT_TEXT_COLOR = colors.text.primary;
export const TOAST_DEFAULT_SECONDARY_TEXT_COLOR = colors.text.primary;

/** Typography style for toast text (both lines). */
export const TOAST_TYPOGRAPHY = typography.c1Regular;

/** Layout constants. */
export const TOAST_BORDER_RADIUS = radius.lg;
export const TOAST_HORIZONTAL_PADDING = spacing.sm;
export const TOAST_VERTICAL_PADDING = spacing.s;
export const TOAST_GAP = spacing.s;
export const TOAST_ICON_SIZE = 16;

/**
 * Resolve the effective colours for a Toast instance.
 *
 * Precedence: an explicit colour prop always wins for its channel. Otherwise
 * `tone` picks the default — `dark` inverts the icon and both text lines for a
 * dark surface, `light` uses the primary defaults. `backgroundColor` is
 * independent of tone.
 */
export function toastColorsFor(
  backgroundColor?: string,
  iconColor?: string,
  textColor?: string,
  secondaryTextColor?: string,
  tone: ToastTone = 'light',
): { background: string; icon: string; text: string; secondaryText: string } {
  const isDark = tone === 'dark';
  return {
    background: backgroundColor ?? TOAST_DEFAULT_BACKGROUND,
    icon: iconColor ?? (isDark ? colors.icon.inverted : TOAST_DEFAULT_ICON_COLOR),
    text: textColor ?? (isDark ? colors.text.inverted : TOAST_DEFAULT_TEXT_COLOR),
    secondaryText:
      secondaryTextColor ?? (isDark ? colors.text.inverted : TOAST_DEFAULT_SECONDARY_TEXT_COLOR),
  };
}
